/**
 * Camera projection — single source of truth for cyber-grid.frag.glsl.
 * 셰이더는 이 값들을 buildShaderDefines()가 컴파일 직전 주입하는 #define으로 소비한다.
 * camPos = (0, 2.2, 6), camTarget = (0, 0.9, -14.5), fov = 60°
 */

export type Vec3 = readonly [number, number, number];

// 눈높이 2.2 — 제단 윗면(y=0.9)보다 1.3 위라 윗면이 보인다(1.3 일 땐 0.4 위라 몇 px 뿐이었다).
export const CAMERA_POS: Vec3 = [0, 2.2, 6];
export const CAMERA_TARGET: Vec3 = [0, 0.9, -14.5];
export const CAMERA_FOV_DEG = 60;

/** Modal trajectory waypoints — shader receives these via buildShaderDefines().
 *  높이는 눈높이 기준 — 모달이 바닥에 남으면 카메라를 올렸을 때 화면 아래로 밀리고 사다리꼴이 된다.
 *  홀로그램은 공중에 떠 있다(눈높이 2.2 에서 아랫변이 바닥 위 0.9). */
const EYE_Y = CAMERA_POS[1];
export const MODAL_ENTRY: Vec3 = [0, EYE_Y + 0.1, 2.3];
export const MODAL_TOUCH: Vec3 = [0, EYE_Y - 0.1, 2.4];
export const MODAL_REST: Vec3 = [0, EYE_Y, 2.5];
/** Half-extents (shader MODAL_HW / MODAL_HH). HH=1.3 (이전 1.15 +13%). */
export const MODAL_HALF_W = 2.5;
export const MODAL_HALF_H = 1.3;
/** uModalProgress threshold where the modal lifecycle starts (shader MODAL_LC_START) */
export const MODAL_LIFECYCLE_START = 0.58;

/**
 * Lichtdom 빔 배열 — 셰이더 BEAM_COUNT / BEAM_SPACING.
 *
 * 빔은 클립 공간(vClipPos, 늘 [-1,1])에 배치되므로 간격은 **뷰포트 폭에 비례하는 픽셀**이
 * 된다. 1440px 에서 0.24 는 ~173px 열주지만 390px 폰에서는 ~47px 줄무늬 커튼이다. 게다가
 * 3D 기둥은 `halfW = HALF_FOV_TAN * aspect` 로 aspect 를 반영해 세로 폰에서 화면 밖으로
 * 나가는데 빔만 제자리에 남아, 없는 기둥의 빛기둥만 뜬다.
 *
 * 좁은 뷰포트에서는 수를 절반으로 줄이고 간격을 넓혀 열주의 리듬을 유지한다.
 * uniform 이 아니라 #define 인 이유 — GLSL ES 1.00(WebGL1 폴백)은 루프 조건에 상수식만
 * 허용해 `i < uBeamCount` 가 컴파일되지 않고, 상수여야 프래그먼트 비용도 실제로 준다.
 */
export const BEAM_COUNT_WIDE = 8;
export const BEAM_COUNT_NARROW = 4;
export const BEAM_SPACING_WIDE = 0.24;
/** 0.34 → 390px 폰에서 ~66px 간격 (기존 ~47px) */
export const BEAM_SPACING_NARROW = 0.34;

/**
 * 계단 맨 아랫단 앞면의 z — 셰이더 `STAIR_Z0` 과 같은 값이다.
 * 빛기둥 뿌리가 이 바닥선(y=0)에 닿는다. 뒷줄 기둥은 제단과 같은 깊이에 서 있어서, 지평선에서
 * 끊으면 기둥이 제단 윗면 높이에 떠 보였다.
 */
export const STAIR_FRONT_Z = -10;

export interface ShaderDefineOptions {
  /** 좁은(세로) 뷰포트 — 빔·기둥 루프를 절반으로 줄인다 */
  narrow?: boolean;
}

/** GLSL float literal — 정수도 '6.0'처럼 소수점 강제 (GLSL ES int/float 비혼용) */
const fmt = (n: number): string => {
  const s = String(n);
  return s.includes('.') ? s : `${s}.0`;
};
const fmtVec3 = (v: Vec3): string => `vec3(${fmt(v[0])}, ${fmt(v[1])}, ${fmt(v[2])})`;

/**
 * Builds the #define block injected ahead of cyber-grid.frag.glsl at compile
 * time (CyberGridGL). 매크로 이름에 단독 'in'/'out' 토큰 금지 — WebGL1 폴백의
 * \bin\b / \bout\b 치환 정규식과 충돌한다.
 */
export function buildShaderDefines({ narrow = false }: ShaderDefineOptions = {}): string {
  const halfFovTan = Math.tan((CAMERA_FOV_DEG * Math.PI) / 360);
  return (
    [
      `#define BEAM_COUNT ${fmt(narrow ? BEAM_COUNT_NARROW : BEAM_COUNT_WIDE)}`,
      `#define BEAM_SPACING ${fmt(narrow ? BEAM_SPACING_NARROW : BEAM_SPACING_WIDE)}`,
      `#define CAM_POS ${fmtVec3(CAMERA_POS)}`,
      `#define CAM_TARGET ${fmtVec3(CAMERA_TARGET)}`,
      `#define HALF_FOV_TAN ${fmt(halfFovTan)}`,
      // 제단 바닥선의 클립 y — 클립 y 는 화면비와 무관해서 1x1 뷰포트로 투영한다
      `#define BEAM_BASE_Y ${fmt(1 - 2 * projectWorldToScreen([0, 0, STAIR_FRONT_Z], 1, 1).y)}`,
      `#define MODAL_HW ${fmt(MODAL_HALF_W)}`,
      `#define MODAL_HH ${fmt(MODAL_HALF_H)}`,
      `#define MODAL_ENTRY ${fmtVec3(MODAL_ENTRY)}`,
      `#define MODAL_TOUCH ${fmtVec3(MODAL_TOUCH)}`,
      `#define MODAL_REST ${fmtVec3(MODAL_REST)}`,
      `#define MODAL_LC_START ${fmt(MODAL_LIFECYCLE_START)}`,
    ].join('\n') + '\n'
  );
}

const smoothstep = (e0: number, e1: number, x: number) => {
  const t = Math.max(0, Math.min(1, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
};
const lerpVec = (a: Vec3, b: Vec3, t: number): Vec3 => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
  a[2] + (b[2] - a[2]) * t,
];

/**
 * Returns modal world position for a given uModalProgress (0..1).
 * Mirrors the shader's modalLifecycle + entry→touch→rest interpolation.
 */
export function modalPositionAt(progress: number): Vec3 {
  const lc = smoothstep(MODAL_LIFECYCLE_START, 1.0, progress);
  if (lc <= 0) return MODAL_ENTRY;
  if (lc < 0.5) {
    return lerpVec(MODAL_ENTRY, MODAL_TOUCH, smoothstep(0, 1, lc / 0.5));
  }
  return lerpVec(MODAL_TOUCH, MODAL_REST, smoothstep(0, 1, (lc - 0.5) / 0.5));
}

const sub = (a: Vec3, b: Vec3): Vec3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const norm = (v: Vec3): Vec3 => {
  const m = Math.hypot(v[0], v[1], v[2]) || 1;
  return [v[0] / m, v[1] / m, v[2] / m];
};
const cross = (a: Vec3, b: Vec3): Vec3 => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];
const dot = (a: Vec3, b: Vec3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];

export interface ScreenProjection {
  x: number;
  y: number;
  pixelsPerUnit: number;
}

export function projectWorldToScreen(
  world: Vec3,
  viewportWidth: number,
  viewportHeight: number
): ScreenProjection {
  const forward = norm(sub(CAMERA_TARGET, CAMERA_POS));
  const right = norm(cross(forward, [0, 1, 0]));
  const up = cross(right, forward);

  const aspect = viewportWidth / viewportHeight;
  const halfH = Math.tan((CAMERA_FOV_DEG * Math.PI) / 360);
  const halfW = halfH * aspect;

  const delta = sub(world, CAMERA_POS);
  const camX = dot(delta, right);
  const camY = dot(delta, up);
  const camZ = Math.max(dot(delta, forward), 0.0001);

  const clipX = camX / (camZ * halfW);
  const clipY = camY / (camZ * halfH);

  const x = (clipX + 1) * 0.5 * viewportWidth;
  const y = (1 - clipY) * 0.5 * viewportHeight;
  const pixelsPerUnit = (0.5 * viewportHeight) / (camZ * halfH);

  return { x, y, pixelsPerUnit };
}
